import { useState } from "react";
import { useRouter } from "next/router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PROJECTS_LIST_QUERY_KEY } from "src/domains/projects-list/project-query/projects.queries";
import { PROJECT_USER_ROLE_QUERY_KEY } from "src/domains/myPageContent/accountInfo/user-query/user.queries";
import {
  usersOfProjectQueryKey,
  useTransferProjectOwner,
} from "src/domains/myPageContent/userManage/userManage-query/usersOfProject.queries";
import type { ProjectId } from "src/domains/myPageContent/userManage/userManage-query/usersOfProject.modal";
import { OwnerMigrationApiError } from "src/domains/myPageContent/userManage/userManage-query/ownerMigration.repository";
import { stripStatusPrefix } from "src/domains/shared/api/stripStatusPrefix";
import notice from "src/domains/shared/utils/notice";

/** 이전한 프로젝트 안에 있던 사용자는 더 접근할 수 없으므로 여기로 보낸다. */
const PROJECT_LIST_ROUTE = "/projects-list";

/** 지금 라우트가 그 프로젝트 화면 안인가 — 라우트의 `projectId` 가 배열로 오면 첫 값으로 본다. */
const isViewingProject = (
  query: Record<string, string | string[] | undefined>,
  projectId: number
): boolean => {
  const routeProjectIdRaw = query.projectId;
  const routeProjectId = Array.isArray(routeProjectIdRaw) ? routeProjectIdRaw[0] : routeProjectIdRaw;
  return routeProjectId !== undefined && Number(routeProjectId) === projectId;
};

export type TransferErrorKey =
  | "myPageModal.permission.transfer.invalidTarget"
  | "myPageModal.permission.transfer.notFound"
  | "myPageModal.permission.transfer.titleConflict"
  | "myPageModal.permission.transfer.failed";

/** 서버 `TITLE_CONFLICT` 문구 — 같은 409 라도 DB 잠금 경합(교착·대기 초과)은 다른 문구로 오므로 이것만 이름 충돌로 본다. */
const TITLE_CONFLICT_MESSAGE = "동일한 이름의 프로젝트가 이미 존재합니다.";

/** BRACKETED 접두(`[409 Conflict] `)를 뗀 서버 문구로 판정한다 — folders.queries·equipmentWriteFailure 와 같은 규약. */
const isTitleConflict = (message: string): boolean =>
  stripStatusPrefix(message) === TITLE_CONFLICT_MESSAGE;

/**
 * 소유권 이전 실패를 안내 문구 키로 가른다 — 계약(backend-api migration POST):
 * - 400: 자기 자신·admin 계정, 또는 요청 형식 오류 → `invalidTarget`
 * - 404: 대상 이메일이 가입자가 아님(미가입·탈퇴), 또는 요청자가 그 프로젝트의 owner 가 아님(비owner·비멤버 —
 *   예: 응답을 놓친 채 이미 끝난 이전을 다시 요청) → `notFound`. 두 원인이 같은 404 로 오므로 문구가 둘을 함께 밝힌다.
 * - 409 이름 충돌: 새 소유자가 삭제되지 않은 같은 이름(title)의 프로젝트를 이미 가짐(서버 유일 제약 owner_id+title) →
 *   `titleConflict` — 라이브 OpenAPI 응답 목록에는 빠져 있는 분기다. 같은 409 가 DB 잠금 경합(교착·대기 초과)에도
 *   오므로 접두(`[409 Conflict] `)를 뗀 서버 문구가 "동일한 이름의 프로젝트가 이미 존재합니다." 일 때만 이름 충돌로 보고, 그 밖의 409 는
 *   `failed`(잠시 후 재시도)로 둔다.
 * - 그 밖(403·5xx·네트워크) → `failed`
 */
export const resolveTransferErrorKey = (error: unknown): TransferErrorKey => {
  if (error instanceof OwnerMigrationApiError) {
    if (error.status === 400) return "myPageModal.permission.transfer.invalidTarget";
    if (error.status === 404) return "myPageModal.permission.transfer.notFound";
    if (error.status === 409 && isTitleConflict(error.message)) {
      return "myPageModal.permission.transfer.titleConflict";
    }
  }
  return "myPageModal.permission.transfer.failed";
};

/**
 * 프로젝트 소유권 이전 — 창 열기/닫기·실행·실패 안내 키. 멤버 관리(`useUserManagement`)와 소비자가 겹치지 않아
 * 따로 둔다: 이 훅만 라우터(이전 뒤 목록 이동)와 다른 도메인 캐시 무효화를 알고, 멤버 관리는 이 상태를 읽지 않는다.
 * `selectedProject` 는 멤버 관리가 고른 프로젝트(문자열 id)를 그대로 받는다.
 */
export const useProjectOwnerTransfer = (selectedProject: string) => {
  const { t } = useTranslation("projects");
  const router = useRouter();
  const queryClient = useQueryClient();
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferError, setTransferError] = useState<TransferErrorKey | "">("");
  const { mutate: transferOwner, isLoading: isTransferring } = useTransferProjectOwner();

  // 창 열기/닫기와 실행. 진행 중에는 닫지 않는다(창의 잠금과 같은 규칙).
  const handleOpenTransferDialog = () => {
    setTransferError("");
    setShowTransferDialog(true);
  };

  const handleCloseTransferDialog = () => {
    if (isTransferring) return;
    setShowTransferDialog(false);
    setTransferError("");
  };

  const clearTransferError = () => setTransferError("");

  const handleTransferOwner = ({ email }: { email: string }) => {
    const projectId = Number(selectedProject);
    setTransferError("");
    transferOwner(
      { projectId, newOwnerEmail: email },
      {
        onSuccess: () => {
          // 구 주인은 이 프로젝트에서 빠진다 — 소유·전체 목록(접두 키)·내 역할·멤버 목록을 다시 읽는다.
          queryClient.invalidateQueries({ queryKey: PROJECTS_LIST_QUERY_KEY });
          queryClient.invalidateQueries({ queryKey: PROJECT_USER_ROLE_QUERY_KEY });
          queryClient.invalidateQueries({
            queryKey: usersOfProjectQueryKey(selectedProject as ProjectId),
          });
          notice("success", t("myPage.messages.ownerTransferred", { ns: "projects" }));
          setShowTransferDialog(false);
          // 지금 그 프로젝트 화면 안이면 모든 조회가 404 가 되므로 목록으로 옮긴다.
          if (isViewingProject(router.query, projectId)) void router.replace(PROJECT_LIST_ROUTE);
        },
        onError: (error: unknown) => {
          setTransferError(resolveTransferErrorKey(error));
        },
      }
    );
  };

  return {
    showTransferDialog,
    /** 소유권 이전 실패 안내 키 — 창 안 오류 자리에 보인다. 빈 문자열이면 오류 없음. */
    transferError,
    /**
     * 이전 실패 안내만 비운다(창은 열린 채). 이전 창은 오류 문구가 있는 동안 실행을 막으므로, 화면이 입력을
     * 고칠 때 불러야 다시 실행할 수 있다(멤버 추가 쪽 `setEmailError("")` 와 같은 자리).
     */
    clearTransferError,
    /** Mutation 진행 상태 — 버튼 비활성/라벨 전환용. */
    isTransferring,
    handleOpenTransferDialog,
    handleCloseTransferDialog,
    handleTransferOwner,
  };
};
